import { useEffect, useRef, useCallback } from 'react';
import { 
  focusManager, 
  keyboardShortcuts, 
  ScreenReaderUtils, 
  AriaUtils,
  VisualAccessibilityUtils 
} from '../utils/accessibility';

/**
 * Custom hook for accessibility features in CV Builder
 */
export const useAccessibility = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * Sets up keyboard navigation for the container
   */
  const setupKeyboardNavigation = useCallback((container: HTMLElement) => {
    if (cleanupRef.current) {
      cleanupRef.current();
    }
    cleanupRef.current = focusManager.setupKeyboardNavigation(container);
  }, []);

  /**
   * Announces a message to screen readers
   */
  const announceToScreenReader = useCallback((
    message: string, 
    priority: 'polite' | 'assertive' = 'polite'
  ) => {
    focusManager.announceToScreenReader(message, priority);
  }, []);

  /**
   * Focuses the first error in a form
   */
  const focusFirstError = useCallback((container?: HTMLElement) => {
    const targetContainer = container || containerRef.current;
    if (targetContainer) {
      return focusManager.focusFirstError(targetContainer);
    }
    return false;
  }, []);

  /**
   * Sets up form section with proper ARIA attributes
   */
  const setupFormSection = useCallback((container: HTMLElement, sectionName: string) => {
    AriaUtils.setupFormSection(container, sectionName);
  }, []);

  /**
   * Sets up progress indicator with ARIA attributes
   */
  const setupProgressIndicator = useCallback((element: HTMLElement, current: number, total: number) => {
    AriaUtils.setupProgressIndicator(element, current, total);
  }, []);

  /**
   * Creates progress announcement for screen readers
   */
  const announceProgress = useCallback((current: number, total: number, sectionName?: string) => {
    const announcement = ScreenReaderUtils.createProgressAnnouncement(current, total, sectionName);
    announceToScreenReader(announcement, 'polite');
  }, [announceToScreenReader]);

  /**
   * Announces validation errors
   */
  const announceValidationErrors = useCallback((errors: string[]) => {
    if (errors.length > 0) {
      const announcement = ScreenReaderUtils.createValidationErrorAnnouncement(errors);
      announceToScreenReader(announcement, 'assertive');
    }
  }, [announceToScreenReader]);

  /**
   * Announces successful actions
   */
  const announceSuccess = useCallback((action: string) => {
    const announcement = ScreenReaderUtils.createSuccessAnnouncement(action);
    announceToScreenReader(announcement, 'polite');
  }, [announceToScreenReader]);

  /**
   * Announces loading states
   */
  const announceLoading = useCallback((action: string) => {
    const announcement = ScreenReaderUtils.createLoadingAnnouncement(action);
    announceToScreenReader(announcement, 'polite');
  }, [announceToScreenReader]);

  /**
   * Sets up tab list with proper ARIA attributes
   */
  const setupTabList = useCallback((container: HTMLElement) => {
    AriaUtils.setupTabList(container);
  }, []);

  /**
   * Updates tab selection state
   */
  const updateTabSelection = useCallback((container: HTMLElement, selectedIndex: number) => {
    AriaUtils.updateTabSelection(container, selectedIndex);
  }, []);

  /**
   * Sets up form field validation with ARIA attributes
   */
  const setupFormValidation = useCallback((field: HTMLElement, errorId?: string) => {
    AriaUtils.setupFormValidation(field, errorId);
  }, []);

  /**
   * Checks if user prefers reduced motion
   */
  const prefersReducedMotion = useCallback(() => {
    return VisualAccessibilityUtils.prefersReducedMotion();
  }, []);

  /**
   * Checks if user prefers high contrast
   */
  const prefersHighContrast = useCallback(() => {
    return VisualAccessibilityUtils.prefersHighContrast();
  }, []);

  /**
   * Registers keyboard shortcuts
   */
  const registerShortcut = useCallback((key: string, callback: () => void) => {
    keyboardShortcuts.register(key, callback);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  return {
    containerRef,
    setupKeyboardNavigation,
    announceToScreenReader,
    focusFirstError,
    setupFormSection,
    setupProgressIndicator,
    announceProgress,
    announceValidationErrors,
    announceSuccess,
    announceLoading,
    setupTabList,
    updateTabSelection,
    setupFormValidation,
    prefersReducedMotion,
    prefersHighContrast,
    registerShortcut
  };
};

/**
 * Hook for managing focus within a specific component
 */
export const useFocusManagement = (initialFocusSelector?: string) => {
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Sets focus to the initial element or first focusable element
   */
  const setInitialFocus = useCallback(() => {
    if (!containerRef.current) return;

    let targetElement: HTMLElement | null = null;

    if (initialFocusSelector) {
      targetElement = containerRef.current.querySelector(initialFocusSelector);
    }

    if (!targetElement) {
      const focusableElements = focusManager.getFocusableElements(containerRef.current);
      targetElement = focusableElements[0] || null;
    }

    if (targetElement) {
      targetElement.focus();
    }
  }, [initialFocusSelector]);

  /**
   * Traps focus within the container
   */
  const trapFocus = useCallback(() => {
    if (containerRef.current) {
      return focusManager.setupKeyboardNavigation(containerRef.current);
    }
    return () => {};
  }, []);

  /**
   * Moves focus to next focusable element
   */
  const focusNext = useCallback(() => {
    if (!containerRef.current) return;
    
    const focusableElements = focusManager.getFocusableElements(containerRef.current);
    const currentIndex = focusableElements.findIndex(el => el === document.activeElement);
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    
    if (focusableElements[nextIndex]) {
      focusableElements[nextIndex].focus();
    }
  }, []);

  /**
   * Moves focus to previous focusable element
   */
  const focusPrevious = useCallback(() => {
    if (!containerRef.current) return;
    
    const focusableElements = focusManager.getFocusableElements(containerRef.current);
    const currentIndex = focusableElements.findIndex(el => el === document.activeElement);
    const prevIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
    
    if (focusableElements[prevIndex]) {
      focusableElements[prevIndex].focus();
    }
  }, []);

  return {
    containerRef,
    setInitialFocus,
    trapFocus,
    focusNext,
    focusPrevious
  };
};

/**
 * Hook for managing live regions and announcements
 */
export const useLiveRegion = () => {
  const liveRegionRef = useRef<HTMLElement>(null);

  /**
   * Creates a live region element
   */
  const createLiveRegion = useCallback((priority: 'polite' | 'assertive' = 'polite') => {
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    
    document.body.appendChild(liveRegion);
    (liveRegionRef as React.MutableRefObject<HTMLElement | null>).current = liveRegion;
    
    return liveRegion;
  }, []);

  /**
   * Announces a message through the live region
   */
  const announce = useCallback((message: string) => {
    if (!liveRegionRef.current) {
      createLiveRegion();
    }
    
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = message;
    }
  }, [createLiveRegion]);

  /**
   * Clears the live region
   */
  const clear = useCallback(() => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = '';
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const currentElement = liveRegionRef.current;
      if (currentElement && currentElement.parentNode) {
        currentElement.parentNode.removeChild(currentElement);
      }
    };
  }, []);

  return {
    announce,
    clear,
    createLiveRegion
  };
};